import { useFormStore } from '../../lib/store';
import ModuleCard from '../ModuleCard';
import clsx from 'clsx';
import { formatNtd } from '../ui';

interface RaceSelectionModuleProps {
  module: {
    id: string;
    name: string;
    nameEn?: string;
    fields: any[];
    settings: {
      displayStyle?: 'cards' | 'list' | 'grid';
      showOriginalPrice?: boolean;
      showDiscount?: boolean;
      discountLabel?: string;
      categories?: {
        id: string;
        name: string;
        nameEn?: string;
        badge?: string;
        badgeColor?: string;
        originalPrice: number;
        discountedPrice: number;
        description?: string;
        participantCount?: number;
      }[];
      shippingOptions?: { id: string; label: string; labelEn?: string; price: number }[];
      images?: string[];
    };
  };
  event: any;
}

export default function RaceSelectionModule({ module }: RaceSelectionModuleProps) {
  const { selectedCategoryId, setCategory, selectedShippingId, setShipping, errors } = useFormStore();
  const settings = module.settings || {};
  const categories = settings.categories || [];
  const shippingOptions = settings.shippingOptions || [];
  const categoryError = errors['category'];

  return (
    <ModuleCard title={module.name} subtitle={settings.discountLabel || '每人限報一組'}>
      {settings.images && settings.images.length > 0 && (
        <div className="flex flex-col gap-2">
          {settings.images.map((img, idx) => <img key={idx} src={img} alt="" className="w-full rounded-tile" />)}
        </div>
      )}

      <div id="field-category" role="radiogroup" aria-label={module.name} className="flex flex-col gap-3">
        {categories.map((category) => {
          const on = selectedCategoryId === category.id;
          const price = category.discountedPrice ?? category.originalPrice;
          const hasDiscount = category.originalPrice != null && category.originalPrice !== price;
          return (
            <label
              key={category.id}
              className={clsx(
                'flex flex-col gap-3 p-[18px] rounded-card border-2 cursor-pointer transition-colors',
                on ? 'border-primary bg-primary-soft' : 'border-transparent bg-soft',
                categoryError && !selectedCategoryId && '!bg-bad-soft',
              )}
            >
              <div className="flex items-center gap-3">
                <input type="radio" name="category" value={category.id} checked={on} onChange={() => setCategory(category.id)} className="w-6 h-6 shrink-0" />
                <div className="flex-1 min-w-0 flex flex-col leading-tight">
                  <span className="text-[18px] font-black">{category.name}</span>
                  {(category.nameEn || category.description) && (
                    <span className="text-xs text-muted mt-1">{category.description || category.nameEn}</span>
                  )}
                </div>
                <div className="flex flex-col items-end leading-none shrink-0">
                  {settings.showOriginalPrice !== false && hasDiscount && (
                    <span className="tabular text-xs text-faint line-through">{category.originalPrice.toLocaleString('zh-TW')}</span>
                  )}
                  <span className={clsx('tabular text-[20px] font-black mt-0.5', on && 'text-primary')}>{price.toLocaleString('zh-TW')}</span>
                </div>
              </div>
              {(category.badge || (settings.showDiscount && hasDiscount)) && (
                <div className="flex flex-wrap gap-1.5">
                  {category.badge && (
                    <span className="h-[26px] px-2.5 rounded-full text-white text-xs font-bold inline-flex items-center" style={{ background: category.badgeColor || 'var(--color-primary)' }}>
                      {category.badge}
                    </span>
                  )}
                  {settings.showDiscount && hasDiscount && (
                    <span className="h-[26px] px-2.5 rounded-full bg-white text-xs inline-flex items-center">
                      {settings.discountLabel || '團報優惠'} 省 {formatNtd(category.originalPrice - price)}
                    </span>
                  )}
                </div>
              )}
            </label>
          );
        })}
      </div>
      {categoryError && <p role="alert" className="text-xs text-bad -mt-2">{categoryError}</p>}

      {shippingOptions.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-[13px] font-bold">寄送方式</span>
          <div role="radiogroup" aria-label="寄送方式" className="grid grid-cols-2 gap-2">
            {shippingOptions.map((option) => {
              const on = selectedShippingId === option.id;
              return (
                <label key={option.id} className={clsx(
                  'flex flex-col items-center justify-center gap-0.5 h-[60px] rounded-[14px] border-2 cursor-pointer transition-colors',
                  on ? 'border-primary bg-primary-soft' : 'border-transparent bg-soft',
                )}>
                  <input type="radio" name="shipping" value={option.id} checked={on} onChange={() => setShipping(option.id)} className="sr-only" />
                  <span className="text-[14px] font-bold">{option.label}</span>
                  <span className="tabular text-xs text-muted">{option.price === 0 ? '免費' : `+${formatNtd(option.price)}`}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </ModuleCard>
  );
}
